import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  getDocs,
  serverTimestamp,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  Order, 
  OrderCreateData, 
  OrderUpdateData, 
  OrderSummary, 
  OrderItemSummary,
  OrderFilters, 
  OrderStats,
  OrderService,
  OrderStatus,
  PaymentStatus,
  OrderItem,
  RefundInfo,
  ManualOrderCreateData,
  ManualOrderLineItem,
  CartItem,
  datesEqual,
  getDateKey
} from '../types';
import { stripeService } from './stripeService';
import { availabilityService } from './availabilityService';
import { v4 as uuidv4 } from 'uuid';

/**
 * OrderService - Handles all order operations with Firebase integration
 */
class OrderServiceImpl implements OrderService {
  private static instance: OrderServiceImpl;
  private readonly COLLECTION_NAME = 'orders';
  private readonly ORDER_COUNTER_DOC = 'order_counter';

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): OrderServiceImpl {
    if (!OrderServiceImpl.instance) {
      OrderServiceImpl.instance = new OrderServiceImpl();
    }
    return OrderServiceImpl.instance;
  }

  /**
   * Create a new order
   */
  public async createOrder(data: OrderCreateData): Promise<Order> {
    try {
      const orderId = uuidv4();
      const orderNumber = await this.generateOrderNumber();
      const now = new Date();

      // Create or get existing Stripe customer
      let customerId = data.customer.id;
      if (!customerId) {
        customerId = await stripeService.createOrGetCustomer(data.customer);
      }

      // Calculate proper shipping using the shipping service
      const shipping = await this.calculateShippingWithService(data.items, data.deliveryOption, data.deliveryAddress);
      
      // Calculate order totals properly using VAT settings
      const subtotal = this.calculateSubtotal(data.items);
      const { vatAmount, vatPercentage, isInclusive } = await this.calculateVatWithSettings(subtotal);
      
      // Calculate totals with coupon discount
      const couponDiscount = data.couponDiscount || 0;
      const originalTotal = subtotal + vatAmount + shipping;
      const totalAmount = Math.max(0, originalTotal - couponDiscount);

      // NOTE: Payment intent should already be created by PaymentForm
      // We should not create a new one here to avoid duplicates
      // The paymentIntentId should be passed from the checkout process

      // Create order items
      const orderItems: OrderItem[] = data.items.map(item => {
        console.log('🔍 OrderService.createOrder - Processing cart item:', {
          id: item.id,
          name: item.name,
          hasImageOnCake: item.hasImageOnCake,
          cakeImageUrl: item.cakeImageUrl,
          hasSampleImage: item.hasSampleImage,
          sampleImageUrl: item.sampleImageUrl
        });
        
        return {
          ...item,
          orderId,
          status: 'pending' as OrderStatus,
          estimatedCompletion: this.calculateEstimatedCompletion(
            data.deliveryDate ? (data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : undefined
          )
        };
      });

      // Create order document
      const windowItem = data.items.find((item) => item.timeWindowStart && item.timeWindowEnd) || data.items[0];
      const windowStart = windowItem?.timeWindowStart;
      const windowEnd = windowItem?.timeWindowEnd;

      console.log('🛒 OrderService.createOrder - Creating order with data:', {
        deliveryOption: data.deliveryOption,
        hasDeliveryAddress: !!data.deliveryAddress,
        deliveryAddress: data.deliveryAddress,
        hasDeliveryInstructions: !!data.deliveryInstructions,
        deliveryInstructions: data.deliveryInstructions
      });
      
      const order: Order = {
        id: orderId,
        orderNumber,
        customer: {
          ...data.customer,
          id: customerId
        },
        items: orderItems,
        payment: {
          id: data.paymentMethodId, // Use the payment intent ID from checkout
          status: 'succeeded', // Payment is confirmed before order creation
          amount: totalAmount,
          currency: 'eur',
          method: 'card',
          createdAt: now,
          updatedAt: now
        },
        status: 'confirmed', // Order is confirmed since payment succeeded
        subtotal: subtotal,
        vat: vatAmount,
        vatPercentage: vatPercentage,
        isInclusive: isInclusive,
        shipping: shipping,
        coupon: data.coupon,
        couponDiscount: couponDiscount,
        originalTotal: originalTotal,
        total: totalAmount,
        deliveryOption: data.deliveryOption,
        deliveryDate: data.deliveryDate ? (data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : undefined,
        // Only include deliveryAddress for delivery orders
        ...(data.deliveryOption === 'delivery' && data.deliveryAddress && { deliveryAddress: data.deliveryAddress }),
        // Only include deliveryInstructions if provided
        ...(data.deliveryInstructions && { deliveryInstructions: data.deliveryInstructions }),
        ...(data.deliveryOption === 'delivery'
          ? {
              ...(windowStart && { deliveryStartTime: windowStart }),
              ...(windowEnd && { deliveryEndTime: windowEnd })
            }
          : {
              ...(windowStart && { collectionStartTime: windowStart }),
              ...(windowEnd && { collectionEndTime: windowEnd })
            }),
        createdAt: now,
        updatedAt: now,
        source: 'website',
        version: '1.0.0'
      };

      // Save to Firebase
      console.log('🛒 OrderService.createOrder - Final order object before Firebase save:', {
        id: order.id,
        deliveryOption: order.deliveryOption,
        hasDeliveryAddress: 'deliveryAddress' in order,
        deliveryAddress: order.deliveryAddress,
        hasDeliveryInstructions: 'deliveryInstructions' in order,
        deliveryInstructions: order.deliveryInstructions
      });
      
      const orderRef = doc(db, this.COLLECTION_NAME, orderId);
      
      // Prepare Firebase data, filtering out undefined values
      const firebaseData: any = {
        ...order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        'deliveryDate': data.deliveryDate ? Timestamp.fromDate(data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : null,
        collectionStartTime: order.collectionStartTime ?? null,
        collectionEndTime: order.collectionEndTime ?? null,
        deliveryStartTime: order.deliveryStartTime ?? null,
        deliveryEndTime: order.deliveryEndTime ?? null,
        'payment.createdAt': serverTimestamp(),
        'payment.updatedAt': serverTimestamp(),
        // Handle coupon data
        'coupon': data.coupon ? {
          ...data.coupon,
          validFrom: data.coupon.validFrom ? Timestamp.fromDate(data.coupon.validFrom) : null,
          validUntil: data.coupon.validUntil ? Timestamp.fromDate(data.coupon.validUntil) : null,
          createdAt: data.coupon.createdAt ? Timestamp.fromDate(data.coupon.createdAt) : null,
          updatedAt: data.coupon.updatedAt ? Timestamp.fromDate(data.coupon.updatedAt) : null
        } : null,
        'items': orderItems.map(item => {
          console.log('🔍 OrderService.createOrder - Preparing Firebase item:', {
            id: item.id,
            name: item.name,
            hasImageOnCake: item.hasImageOnCake,
            cakeImageUrl: item.cakeImageUrl,
            hasSampleImage: item.hasSampleImage,
            sampleImageUrl: item.sampleImageUrl
          });
          
          // Explicitly include image URLs in the Firebase data
          return {
            ...item,
            addedAt: Timestamp.fromDate(item.addedAt),
            updatedAt: Timestamp.fromDate(item.updatedAt),
            deliveryDate: item.deliveryDate ? Timestamp.fromDate(item.deliveryDate instanceof Date ? item.deliveryDate : new Date(item.deliveryDate)) : null,
            estimatedCompletion: item.estimatedCompletion ? Timestamp.fromDate(item.estimatedCompletion) : null,
            // Ensure image URLs are explicitly included
            hasImageOnCake: item.hasImageOnCake || false,
            cakeImageUrl: item.cakeImageUrl || null,
            hasSampleImage: item.hasSampleImage || false,
            sampleImageUrl: item.sampleImageUrl || null,
            timeWindowStart: item.timeWindowStart || null,
            timeWindowEnd: item.timeWindowEnd || null
          };
        })
      };

      // deliveryInstructions is already handled in the order object creation above

      await setDoc(orderRef, firebaseData);
      await this.recordBookingForConfirmedOrder(order.deliveryDate);

      return order;
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error('Failed to create order');
    }
  }

  /**
   * Create a manual order (admin-created, e.g. phone, in-person).
   * Does not use Stripe; payment method and optional payment ID are stored as provided.
   */
  public async createManualOrder(data: ManualOrderCreateData): Promise<Order> {
    try {
      const orderId = uuidv4();
      const orderNumber = await this.generateOrderNumber();
      const now = new Date();

      // Map manual line items to CartItem shape for storage
      const cartItems: CartItem[] = data.items.map((line) => {
        const totalPrice = Math.round(line.quantity * line.unitPrice * 100) / 100;
        return this.manualLineItemToCartItem(line, orderId, now, totalPrice);
      });

      const subtotal = this.calculateSubtotal(cartItems);
      const { vatAmount, vatPercentage, isInclusive } = await this.calculateVatWithSettings(subtotal);

      let shipping: number;
      // Admin manual orders: shippingAmount is optional and independent of postal code.
      // If it is not provided, default to €0.
      shipping = data.shippingAmount ?? 0;

      const originalTotal = subtotal + vatAmount + shipping;

      // Support admin-only discount helpers (fixed amount or percentage) applied to total cost.
      let discountAmount = data.couponDiscount ?? 0;
      if (data.adminDiscountType && data.adminDiscountValue !== undefined && data.adminDiscountValue !== null) {
        if (data.adminDiscountType === 'percentage') {
          discountAmount = (originalTotal * data.adminDiscountValue) / 100;
        } else {
          // 'fixed' => euros to subtract
          discountAmount = data.adminDiscountValue;
        }
      }

      const totalAmount = Math.max(0, Math.round((originalTotal - discountAmount) * 100) / 100);

      const orderItems: OrderItem[] = cartItems.map((item) => ({
        ...item,
        orderId,
        status: 'pending' as OrderStatus,
        estimatedCompletion: this.calculateEstimatedCompletion(
          data.deliveryDate ? (data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : undefined
        )
      }));

      const paymentId = data.paymentId?.trim() || `manual-${data.paymentMethod}-${orderId.slice(0, 8)}`;

      const order: Order = {
        id: orderId,
        orderNumber,
        customer: {
          ...data.customer,
          id: data.customer.id || undefined
        },
        items: orderItems,
        payment: {
          id: paymentId,
          status: 'succeeded',
          amount: totalAmount,
          currency: 'eur',
          method: data.paymentMethod,
          createdAt: now,
          updatedAt: now
        },
        status: 'confirmed',
        subtotal,
        vat: vatAmount,
        vatPercentage,
        isInclusive,
        shipping,
        coupon: data.coupon,
        couponDiscount: discountAmount,
        originalTotal,
        total: totalAmount,
        deliveryOption: data.deliveryOption,
        deliveryDate: data.deliveryDate ? (data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : undefined,
        ...(data.deliveryOption === 'delivery' && data.deliveryAddress && { deliveryAddress: data.deliveryAddress }),
        ...(data.deliveryInstructions && { deliveryInstructions: data.deliveryInstructions }),
        ...(data.collectionStartTime && { collectionStartTime: data.collectionStartTime }),
        ...(data.collectionEndTime && { collectionEndTime: data.collectionEndTime }),
        ...(data.deliveryStartTime && { deliveryStartTime: data.deliveryStartTime }),
        ...(data.deliveryEndTime && { deliveryEndTime: data.deliveryEndTime }),
        createdAt: now,
        updatedAt: now,
        source: 'admin',
        version: '1.0.0',
        ...(data.adminNotes && { notes: data.adminNotes })
      };

      const orderRef = doc(db, this.COLLECTION_NAME, orderId);
      // Firestore does not accept undefined; explicitly set every optional field to null when missing
      const customerForFirestore = {
        email: order.customer.email,
        firstName: order.customer.firstName,
        lastName: order.customer.lastName,
        id: order.customer.id ?? null,
        phone: order.customer.phone ?? null,
        deliveryInstructions: order.customer.deliveryInstructions ?? null,
        deliveryOption: order.customer.deliveryOption ?? null,
        address: order.customer.address != null
          ? {
              line1: order.customer.address.line1 ?? '',
              line2: order.customer.address.line2 ?? null,
              city: order.customer.address.city ?? '',
              state: order.customer.address.state ?? null,
              postalCode: order.customer.address.postalCode ?? '',
              country: order.customer.address.country ?? ''
            }
          : null
      };
      const firebaseData: any = {
        ...order,
        customer: customerForFirestore,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deliveryDate: data.deliveryDate ? Timestamp.fromDate(data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)) : null,
        deliveryAddress: order.deliveryAddress ?? null,
        deliveryInstructions: order.deliveryInstructions ?? null,
        collectionStartTime: order.collectionStartTime ?? null,
        collectionEndTime: order.collectionEndTime ?? null,
        deliveryStartTime: order.deliveryStartTime ?? null,
        deliveryEndTime: order.deliveryEndTime ?? null,
        notes: order.notes ?? null,
        'payment.createdAt': serverTimestamp(),
        'payment.updatedAt': serverTimestamp(),
        coupon: data.coupon ? {
          ...data.coupon,
          validFrom: data.coupon.validFrom ? Timestamp.fromDate(data.coupon.validFrom) : null,
          validUntil: data.coupon.validUntil ? Timestamp.fromDate(data.coupon.validUntil) : null,
          createdAt: data.coupon.createdAt ? Timestamp.fromDate(data.coupon.createdAt) : null,
          updatedAt: data.coupon.updatedAt ? Timestamp.fromDate(data.coupon.updatedAt) : null
        } : null,
        items: orderItems.map((item) => ({
          ...item,
          addedAt: Timestamp.fromDate(item.addedAt),
          updatedAt: Timestamp.fromDate(item.updatedAt),
          deliveryDate: item.deliveryDate ? Timestamp.fromDate(item.deliveryDate instanceof Date ? item.deliveryDate : new Date(item.deliveryDate)) : null,
          estimatedCompletion: item.estimatedCompletion ? Timestamp.fromDate(item.estimatedCompletion) : null,
          hasImageOnCake: item.hasImageOnCake || false,
          cakeImageUrl: item.cakeImageUrl ?? null,
          hasSampleImage: item.hasSampleImage || false,
          sampleImageUrl: item.sampleImageUrl ?? null
        }))
      };

      await setDoc(orderRef, firebaseData);
      await this.recordBookingForConfirmedOrder(order.deliveryDate);
      return order;
    } catch (error) {
      console.error('Error creating manual order:', error);
      throw new Error('Failed to create manual order');
    }
  }

  /**
   * Map a manual order line item to a CartItem so it displays like website orders (Size, Shape, Layers, Flavors, etc.).
   */
  private manualLineItemToCartItem(
    line: ManualOrderLineItem,
    orderId: string,
    now: Date,
    totalPrice: number
  ): CartItem {
    const id = uuidv4();
    const unitPrice = line.unitPrice;
    return {
      id,
      productId: line.productId || '',
      name: line.name,
      imageUrl: '',
      size: line.size ?? '',
      shape: line.shape ?? '',
      layers: line.layers ?? '',
      flavors: Array.isArray(line.flavors) ? line.flavors : [],
      filling: line.filling ?? '',
      icing: line.icing ?? '',
      baseColor: line.baseColor ?? '',
      pipingColor: line.pipingColor ?? '',
      specialInstructions: line.specialInstructions ?? line.description ?? '',
      addOns: Array.isArray(line.addOns) ? line.addOns : [],
      matchingCupcakes: Array.isArray(line.matchingCupcakes) ? line.matchingCupcakes : [],
      hasImageOnCake: false,
      hasSampleImage: false,
      deliveryOption: 'collection',
      quantity: line.quantity,
      basePrice: unitPrice,
      flavorAdditionalPrice: 0,
      fillingAdditionalPrice: 0,
      icingAdditionalPrice: 0,
      addOnsAdditionalPrice: 0,
      matchingCupcakesAdditionalPrice: 0,
      imageOnCakeAdditionalPrice: 0,
      baseColorAdditionalPrice: 0,
      unitPrice,
      totalPrice,
      addedAt: now,
      updatedAt: now
    };
  }

  /**
   * Get order by ID
   */
  public async getOrder(orderId: string): Promise<Order | null> {
    try {
      const orderRef = doc(db, this.COLLECTION_NAME, orderId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        return null;
      }

      const data = orderSnap.data();
      return this.mapFirestoreOrder(data);
    } catch (error) {
      console.error('Error getting order:', error);
      return null;
    }
  }

  /**
   * Get orders with filtering and pagination
   */
  public async getOrders(
    filters?: OrderFilters, 
    limitCount: number = 50, 
    offset: number = 0
  ): Promise<OrderSummary[]> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      // Apply filters that are safe to query directly without requiring new composite indexes.
      // Status filtering is handled client-side below for reliability in the admin UI.

      if (filters?.dateRange) {
        q = query(
          q, 
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      if (filters?.assignedTo) {
        q = query(q, where('assignedTo', '==', filters.assignedTo));
      }

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'));

      // Apply pagination
      if (offset > 0) {
        // For offset pagination, we'd need to implement cursor-based pagination
        // For now, we'll use limit
        q = query(q, limit(limitCount));
      } else {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const orders: OrderSummary[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const items = data.items || [];
        const itemNames = items.map((item: any) => {
          const quantity = item.quantity || 1;
          const name = item.name || 'Unknown Item';
          return quantity > 1 ? `${quantity}x ${name}` : name;
        });
        
        const itemDetails: OrderItemSummary[] = items.map((item: any) => ({
          name: item.name || 'Unknown Item',
          quantity: item.quantity || 1,
          size: item.size,
          shape: item.shape,
          layers: item.layers,
          flavors: item.flavors || [],
          filling: item.filling,
          icing: item.icing,
          baseColor: item.baseColor,
          pipingColor: item.pipingColor,
          addOns: item.addOns || [],
          matchingCupcakes: item.matchingCupcakes || [],
          hasImageOnCake: item.hasImageOnCake || false,
          specialInstructions: item.specialInstructions,
          unitPrice: item.unitPrice || 0,
          totalPrice: item.totalPrice || 0
        }));
        
        const orderSummary: OrderSummary = {
          id: doc.id,
          orderNumber: data.orderNumber,
          customerName: `${data.customer.firstName} ${data.customer.lastName}`,
          customerEmail: data.customer.email,
          status: data.status,
          total: data.total,
          itemCount: items.length,
          itemNames: itemNames.length > 0 ? itemNames : undefined,
          itemDetails: itemDetails.length > 0 ? itemDetails : undefined,
          createdAt: data.createdAt?.toDate() || new Date(),
          deliveryDate: data.deliveryDate?.toDate(),
          deliveryOption: data.deliveryOption,
          paymentStatus: data.payment?.status || 'pending',
          collectionStartTime: data.collectionStartTime ?? undefined,
          collectionEndTime: data.collectionEndTime ?? undefined,
          deliveryStartTime: data.deliveryStartTime ?? undefined,
          deliveryEndTime: data.deliveryEndTime ?? undefined
        };
        orders.push(orderSummary);
      });

      let filteredOrders = orders;

      // Apply status filter client-side to avoid Firestore "in + orderBy" index issues.
      if (filters?.status && filters.status.length > 0) {
        const selectedStatuses = new Set(filters.status);
        filteredOrders = filteredOrders.filter((order) => selectedStatuses.has(order.status));
      }

      // Apply search filter if provided.
      if (filters?.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredOrders = filteredOrders.filter(order =>
          order.orderNumber.toLowerCase().includes(searchTerm) ||
          order.customerName.toLowerCase().includes(searchTerm) ||
          order.customerEmail.toLowerCase().includes(searchTerm)
        );
      }

      return filteredOrders;
    } catch (error) {
      console.error('Error getting orders:', error);
      return [];
    }
  }

  /**
   * Count active orders (not cancelled/refunded) grouped by delivery/collection date.
   * Used so the availability calendar reflects existing orders, not only newly confirmed ones.
   */
  public async getActiveBookingCountsByDate(
    startDate: Date,
    endDate: Date
  ): Promise<Map<string, number>> {
    const counts = new Map<string, number>();
    const rangeStart = new Date(startDate);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);

    const addOrderDate = (deliveryDate?: Date, status?: OrderStatus) => {
      if (!deliveryDate || !this.isActiveBookingStatus(status)) {
        return;
      }

      if (deliveryDate < rangeStart || deliveryDate > rangeEnd) {
        return;
      }

      const key = getDateKey(deliveryDate);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    };

    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('deliveryDate', '>=', Timestamp.fromDate(rangeStart)),
        where('deliveryDate', '<=', Timestamp.fromDate(rangeEnd))
      );
      const snapshot = await getDocs(q);
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        addOrderDate(this.coerceToDate(data.deliveryDate), data.status);
      });
      return counts;
    } catch (error) {
      console.error('Error counting bookings by delivery date, falling back to recent orders:', error);
    }

    const recentOrders = await this.getOrders(undefined, 500, 0);
    recentOrders.forEach((order) => {
      addOrderDate(order.deliveryDate, order.status);
    });
    return counts;
  }

  /**
   * Update order
   */
  public async updateOrder(orderId: string, data: OrderUpdateData): Promise<Order> {
    try {
      const existingOrder = await this.getOrder(orderId);
      const orderRef = doc(db, this.COLLECTION_NAME, orderId);
      
      const updateData: any = {
        ...data,
        updatedAt: serverTimestamp()
      };

      // Handle date fields
      if (data.estimatedCompletion) {
        updateData.estimatedCompletion = Timestamp.fromDate(data.estimatedCompletion);
      }
      if (data.actualCompletion) {
        updateData.actualCompletion = Timestamp.fromDate(data.actualCompletion);
      }
      if (data.deliveryDate) {
        updateData.deliveryDate = Timestamp.fromDate(
          data.deliveryDate instanceof Date ? data.deliveryDate : new Date(data.deliveryDate)
        );
      }

      await updateDoc(orderRef, updateData);

      if (existingOrder) {
        await this.syncBookingCountForOrderChange(existingOrder, data);
      }

      // Get updated order
      const updatedOrder = await this.getOrder(orderId);
      if (!updatedOrder) {
        throw new Error('Order not found after update');
      }

      return updatedOrder;
    } catch (error) {
      console.error('Error updating order:', error);
      throw new Error('Failed to update order');
    }
  }

  /**
   * Cancel order
   */
  public async cancelOrder(orderId: string, reason?: string): Promise<Order> {
    try {
      const order = await this.getOrder(orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // Update order status
      const updatedOrder = await this.updateOrder(orderId, {
        status: 'cancelled',
        notes: reason ? `Cancelled: ${reason}` : 'Order cancelled'
      });

      // If payment was successful, create refund
      if (order.payment.status === 'succeeded') {
        try {
          await this.refundOrder(orderId, undefined, reason || 'Order cancelled');
        } catch (refundError) {
          console.error('Failed to create refund:', refundError);
          // Don't fail the cancellation if refund fails
        }
      }

      return updatedOrder;
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw new Error('Failed to cancel order');
    }
  }

  /**
   * Delete orders by status (admin cleanup).
   * WARNING: This permanently removes documents from Firestore.
   */
  public async deleteOrdersByStatus(status: OrderStatus): Promise<number> {
    try {
      const q = query(collection(db, this.COLLECTION_NAME), where('status', '==', status));
      const snapshot = await getDocs(q);

      const ids = snapshot.docs.map((docSnap) => docSnap.id);
      if (ids.length === 0) return 0;

      if (this.isActiveBookingStatus(status)) {
        await Promise.all(snapshot.docs.map((docSnap) => {
          const deliveryDate = this.coerceToDate(docSnap.data().deliveryDate);
          return this.releaseBookingForOrder(deliveryDate);
        }));
      }

      const BATCH_LIMIT = 400; // Safety margin under Firestore's 500 ops
      let deleted = 0;

      for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
        const chunk = ids.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);

        chunk.forEach((id) => {
          batch.delete(doc(db, this.COLLECTION_NAME, id));
        });

        await batch.commit();
        deleted += chunk.length;
      }

      return deleted;
    } catch (error) {
      console.error('Error deleting orders by status:', error);
      throw new Error('Failed to delete orders');
    }
  }

  public async deleteOrder(orderId: string): Promise<boolean> {
    try {
      const orderRef = doc(db, this.COLLECTION_NAME, orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) return false;
      const data = orderSnap.data();
      if (this.isActiveBookingStatus(data.status)) {
        await this.releaseBookingForOrder(this.coerceToDate(data.deliveryDate));
      }
      await deleteDoc(orderRef);
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      throw new Error('Failed to delete order');
    }
  }

  /**
   * Refund order
   */
  public async refundOrder(
    orderId: string, 
    amount?: number, 
    reason?: string
  ): Promise<RefundInfo> {
    try {
      const order = await this.getOrder(orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      if (order.payment.status !== 'succeeded') {
        throw new Error('Cannot refund order that has not been paid');
      }

      const refundAmount = amount || order.total;
      const refund = await stripeService.createRefund(
        order.payment.id,
        Math.round(refundAmount * 100), // Convert to cents
        reason || 'requested_by_customer'
      );

      // Update order with refund information
      await this.updateOrder(orderId, {
        status: 'refunded',
        notes: `Refunded: ${reason || 'Customer request'}`
      });

      return refund;
    } catch (error) {
      console.error('Error refunding order:', error);
      throw new Error('Failed to refund order');
    }
  }

  /**
   * Get order statistics
   */
  public async getOrderStats(dateRange?: { start: Date; end: Date }): Promise<OrderStats> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      if (dateRange) {
        q = query(
          q,
          where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
        );
      }

      const querySnapshot = await getDocs(q);
      
      let totalOrders = 0;
      let pendingOrders = 0;
      let completedOrders = 0;
      let cancelledOrders = 0;
      let totalRevenue = 0;
      const ordersByStatus: Record<OrderStatus, number> = {
        pending: 0,
        confirmed: 0,
        processing: 0,
        ready_for_collection: 0,
        out_for_delivery: 0,
        delivered: 0,
        completed: 0,
        cancelled: 0,
        refunded: 0
      };
      const ordersByPaymentStatus: Record<PaymentStatus, number> = {
        pending: 0,
        processing: 0,
        succeeded: 0,
        failed: 0,
        canceled: 0,
        requires_action: 0,
        requires_confirmation: 0,
        requires_payment_method: 0
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalOrders++;
        
        if (data.status === 'pending') pendingOrders++;
        if (data.status === 'completed') completedOrders++;
        if (data.status === 'cancelled') cancelledOrders++;
        
        // Revenue should only include completed orders.
        if (data.status === 'completed' && data.payment?.status === 'succeeded') {
          totalRevenue += data.total || 0;
        }

        // Count by status
        if (data.status && data.status in ordersByStatus) {
          ordersByStatus[data.status as OrderStatus]++;
        }
        if (data.payment?.status && data.payment.status in ordersByPaymentStatus) {
          ordersByPaymentStatus[data.payment.status as PaymentStatus]++;
        }
      });

      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        totalOrders,
        pendingOrders,
        completedOrders,
        cancelledOrders,
        totalRevenue,
        averageOrderValue,
        ordersByStatus,
        ordersByPaymentStatus,
        revenueByMonth: [] // TODO: Implement monthly revenue calculation
      };
    } catch (error) {
      console.error('Error getting order stats:', error);
      return {
        totalOrders: 0,
        pendingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        ordersByStatus: {} as Record<OrderStatus, number>,
        ordersByPaymentStatus: {} as Record<PaymentStatus, number>,
        revenueByMonth: []
      };
    }
  }

  /**
   * Generate order number
   */
  public async generateOrderNumber(): Promise<string> {
    try {
      const counterRef = doc(db, 'counters', this.ORDER_COUNTER_DOC);
      const counterSnap = await getDoc(counterRef);

      let nextNumber = 1;
      if (counterSnap.exists()) {
        nextNumber = (counterSnap.data().count || 0) + 1;
      }

      // Update counter
      await setDoc(counterRef, { count: nextNumber }, { merge: true });

      // Generate order number
      const year = new Date().getFullYear();
      const paddedNumber = nextNumber.toString().padStart(4, '0');
      return `TFR-${year}-${paddedNumber}`;
    } catch (error) {
      console.error('Error generating order number:', error);
      // Fallback to timestamp-based number
      const timestamp = Date.now().toString().slice(-6);
      return `TFR-${new Date().getFullYear()}-${timestamp}`;
    }
  }

  /**
   * Calculate order total
   */
  private calculateOrderTotal(items: any[]): number {
    return items.reduce((total, item) => total + item.totalPrice, 0);
  }

  /**
   * Calculate subtotal
   */
  private calculateSubtotal(items: any[]): number {
    return items.reduce((total, item) => total + item.totalPrice, 0);
  }

  /**
   * Calculate VAT using admin settings
   */
  private async calculateVatWithSettings(subtotal: number): Promise<{ vatAmount: number; vatPercentage: number; isInclusive: boolean }> {
    try {
      const { calculateVatAmount } = await import('./firebase');
      return await calculateVatAmount(subtotal);
    } catch (error) {
      console.error('Error calculating VAT with settings:', error);
      // Fallback to hardcoded calculation if VAT settings fail
      return {
        vatAmount: Math.round(subtotal * 0.23 * 100) / 100,
        vatPercentage: 23,
        isInclusive: false
      };
    }
  }

  /**
   * Calculate VAT (legacy method - kept for backward compatibility)
   */
  private calculateVat(items: any[]): number {
    const subtotal = this.calculateSubtotal(items);
    return Math.round(subtotal * 0.23 * 100) / 100; // 23% VAT
  }

  /**
   * Calculate shipping using the shipping service
   */
  private async calculateShippingWithService(
    items: any[], 
    deliveryOption: string, 
    deliveryAddress?: any
  ): Promise<number> {
    if (deliveryOption === 'collection') {
      return 0;
    }
    
    // If we have a delivery address with postal code, use the shipping service
    if (deliveryAddress?.postalCode) {
      try {
        const { shippingService } = await import('./shippingService');
        return await shippingService.calculateShipping(deliveryAddress.postalCode, 'delivery');
      } catch (error) {
        console.error('Error calculating shipping with service:', error);
        // Fallback to simple calculation
        const subtotal = this.calculateSubtotal(items);
        // return subtotal > 50 ? 0 : 5;
        return 0;
      }
    }
    
    // Fallback to simple calculation
    return 5;
  }

  /**
   * Calculate shipping (legacy method - kept for backward compatibility)
   */
  private calculateShipping(items: any[], deliveryOption: string): number {
    if (deliveryOption === 'collection') {
      return 0;
    }
    
    const subtotal = this.calculateSubtotal(items);
    return subtotal > 50 ? 0 : 5; // Free shipping over €50
  }

  /**
   * Calculate estimated completion
   */
  private calculateEstimatedCompletion(deliveryDate?: Date): Date {
    if (deliveryDate) {
      // If delivery date is provided, estimate completion 2 hours before
      const completion = new Date(deliveryDate);
      completion.setHours(completion.getHours() - 2);
      return completion;
    }
    
    // Default to 24 hours from now
    const completion = new Date();
    completion.setHours(completion.getHours() + 24);
    return completion;
  }

  /**
   * Map Firestore document to Order object
   */
  private mapFirestoreOrder(data: any): Order {
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      confirmedAt: data.confirmedAt?.toDate(),
      completedAt: data.completedAt?.toDate(),
      deliveryDate: data.deliveryDate?.toDate(),
      estimatedCompletion: data.estimatedCompletion?.toDate(),
      actualCompletion: data.actualCompletion?.toDate(),
      payment: {
        ...data.payment,
        createdAt: data.payment?.createdAt?.toDate() || new Date(),
        updatedAt: data.payment?.updatedAt?.toDate() || new Date()
      },
      // Handle coupon data
      coupon: data.coupon ? {
        ...data.coupon,
        validFrom: data.coupon.validFrom?.toDate() || new Date(),
        validUntil: data.coupon.validUntil?.toDate(),
        createdAt: data.coupon.createdAt?.toDate() || new Date(),
        updatedAt: data.coupon.updatedAt?.toDate() || new Date()
      } : undefined,
      couponDiscount: data.couponDiscount || 0,
      originalTotal: data.originalTotal || data.total,
      items: data.items?.map((item: any) => {
        console.log('🔍 OrderService.mapFirestoreOrder - Processing Firebase item:', {
          id: item.id,
          name: item.name,
          hasImageOnCake: item.hasImageOnCake,
          cakeImageUrl: item.cakeImageUrl,
          hasSampleImage: item.hasSampleImage,
          sampleImageUrl: item.sampleImageUrl
        });
        
          // Ensure all fields are properly mapped from Firestore data
          return {
            ...item,
            addedAt: item.addedAt?.toDate() || new Date(),
            updatedAt: item.updatedAt?.toDate() || new Date(),
            deliveryDate: item.deliveryDate?.toDate(),
            estimatedCompletion: item.estimatedCompletion?.toDate(),
            actualCompletion: item.actualCompletion?.toDate(),
            // Explicitly map image-related fields
            hasImageOnCake: item.hasImageOnCake || false,
            cakeImageUrl: item.cakeImageUrl || null,
            hasSampleImage: item.hasSampleImage || false,
            sampleImageUrl: item.sampleImageUrl || null
          };
      }) || []
    };
  }

  private coerceToDate(value: unknown): Date | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : value;
    }

    if (typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const converted = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(converted.getTime()) ? undefined : converted;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const converted = new Date(value);
      return Number.isNaN(converted.getTime()) ? undefined : converted;
    }

    return undefined;
  }

  /**
   * Keep availability counts in sync when an order's status or delivery date changes.
   * Active statuses occupy a slot from pending through completed.
   * Only cancelled and refunded release the slot.
   */
  private async syncBookingCountForOrderChange(
    existingOrder: Order,
    data: OrderUpdateData
  ): Promise<void> {
    const nextStatus = data.status ?? existingOrder.status;
    const wasActive = this.isActiveBookingStatus(existingOrder.status);
    const isActive = this.isActiveBookingStatus(nextStatus);
    const previousDate = existingOrder.deliveryDate;
    const nextDate = data.deliveryDate != null
      ? this.coerceToDate(data.deliveryDate)
      : previousDate;

    if (wasActive && !isActive) {
      await this.releaseBookingForOrder(previousDate);
      return;
    }

    if (!wasActive && isActive) {
      await this.recordBookingForConfirmedOrder(nextDate);
      return;
    }

    if (!isActive || !nextDate) {
      return;
    }

    if (!previousDate) {
      await this.recordBookingForConfirmedOrder(nextDate);
      return;
    }

    if (data.deliveryDate && !datesEqual(previousDate, nextDate)) {
      await this.releaseBookingForOrder(previousDate);
      await this.recordBookingForConfirmedOrder(nextDate);
    }
  }

  private isActiveBookingStatus(status?: OrderStatus): boolean {
    return status != null && status !== 'cancelled' && status !== 'refunded';
  }

  private async recordBookingForConfirmedOrder(deliveryDate?: Date): Promise<void> {
    if (!deliveryDate) {
      return;
    }

    try {
      await availabilityService.recordConfirmedOrderBooking(deliveryDate);
    } catch (error) {
      console.error('Failed to increment availability booking count:', error);
    }
  }

  private async releaseBookingForOrder(deliveryDate?: Date): Promise<void> {
    if (!deliveryDate) {
      return;
    }

    try {
      await availabilityService.releaseConfirmedOrderBooking(deliveryDate);
    } catch (error) {
      console.error('Failed to decrement availability booking count:', error);
    }
  }
}

// Export singleton instance
export const orderService = OrderServiceImpl.getInstance();

// Export class for testing
export { OrderServiceImpl };
